import React, { useEffect, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';
import service from '../../service';
import type { FieldOption } from '../DynamicCustomForm/types';
import type { StageConditionsFormValues } from '../../types/flowStage';

export type CustomFormFieldType = 'string' | 'boolean' | 'date';

/**
 * A custom-form question offered as a condition field; identity is (customFormId, key).
 * Its answer values live in `choices`, never `options`: react-select renders any option
 * with an `options` array as a group header.
 */
export interface CustomFormFieldOption {
  customFormId: string;
  key: string;
  label: string;
  type: CustomFormFieldType;
  choices: FieldOption[];
}

interface ValueComparisonConditionProps {
  control: any;
  flowStageType?: any;
  index: number;
  onRemove?: () => void;
  showRemove?: boolean;
  autoScroll?: boolean;
  // 'stacked' is the original labelled column (sidebar); 'row' is the compact
  // grid cell used inside the drawer's resource-grouped layout.
  variant?: 'stacked' | 'row';
  // Hide the Resource select — used when the resource is fixed by the group header.
  hideResource?: boolean;
  // 1-based index shown in the row layout (position within its resource group).
  displayIndex?: number;
  // Questions of the stage's custom form(s); the field list for `customform` rows.
  customFormFields?: CustomFormFieldOption[];
  // Writes the row's customFormId alongside resourceField for `customform` rows.
  setValue?: UseFormSetValue<StageConditionsFormValues>;
}

export const FISH_RESOURCE = { value: 'fish', label: 'Fish' };

export const RESOURCE_OPTIONS = [
  FISH_RESOURCE,
  { value: 'event', label: 'Event' },
  { value: 'activity', label: 'Activity' },
];

// Offered only on stages fed by a custom form, so it is not part of RESOURCE_OPTIONS.
export const CUSTOM_FORM_RESOURCE = { value: 'customform', label: 'Custom form' };

const STRING_OPERATOR_OPTIONS = [
  { value: 'includes', label: 'Includes' },
  { value: 'not includes', label: 'Not Includes' },
];

const DATE_OPERATOR_OPTIONS = [
  { value: 'before', label: 'Before' },
  { value: 'after', label: 'After' },
  { value: 'within_last', label: 'Within Last' },
  { value: 'older_than', label: 'Older Than' },
];

// On a lapse stage the activity clause is the awaited signal whose ABSENCE fires the
// flow, so its date operators read as the negative. Same stored values — the wording
// just states the lapse: "not in the last 7 days".
const LAPSE_DATE_OPERATOR_OPTIONS = [
  { value: 'within_last', label: 'Not in the last' },
  { value: 'after', label: 'Not since' },
];

const BOOLEAN_OPERATOR_OPTIONS = [
  { value: 'equal', label: 'Is' },
  { value: 'not equal', label: 'Is Not' },
];

const BOOLEAN_VALUE_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

// Relative-window operators carry a { amount, unit } value; before/after carry a date.
const RELATIVE_OPERATORS = ['within_last', 'older_than'];

// Render menus in a portal above the drawer's scroll/overflow containers so the
// dropdown isn't clipped inside the condition row.
const menuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed' as const,
  styles: {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  },
};

export const ValueComparisonCondition: React.FC<
  ValueComparisonConditionProps
> = ({
  control,
  flowStageType,
  index,
  onRemove,
  showRemove = false,
  autoScroll = false,
  variant = 'stacked',
  hideResource = false,
  displayIndex,
  customFormFields = [],
  setValue,
}) => {
  const prefix = `conditions.valueComparisons.${index}` as const;
  const isRow = variant === 'row';

  const isLapse = flowStageType?.name === 'LAPSE_DETECTED';

  // Bring each newly revealed step into view so the user doesn't have to hunt
  // for it in the scrollable sidebar.
  const fieldRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  const scrollIntoView = (el: HTMLElement | null) => {
    if (autoScroll && el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Watch the form values to initialize state from existing data
  const formResource = useWatch({ control, name: `${prefix}.resource` });
  const formResourceField = useWatch({
    control,
    name: `${prefix}.resourceField`,
  });

  const [selectedResource, setSelectedResource] = useState<string>(
    formResource || '',
  );
  const [selectedField, setSelectedField] = useState<string>(
    formResourceField || '',
  );

  // Update local state when form values change
  useEffect(() => {
    if (formResource) {
      setSelectedResource(formResource.toLowerCase());
    }
  }, [formResource]);

  useEffect(() => {
    if (formResourceField) {
      setSelectedField(formResourceField);
    }
  }, [formResourceField]);

  const isCustomForm = selectedResource === CUSTOM_FORM_RESOURCE.value;

  // The same key can exist on two of the stage's forms, so a custom-form field is
  // identified by (customFormId, key), never by key alone.
  const formCustomFormId = useWatch({
    control,
    name: `${prefix}.customFormId`,
  });
  const selectedCustomField = isCustomForm
    ? customFormFields.find(
        (field) =>
          field.customFormId === formCustomFormId &&
          field.key === formResourceField,
      )
    : undefined;

  // Fetch schema for selected resource
  const { data: schemaData } = useQuery({
    queryKey: ['schema', selectedResource],
    queryFn: () => {
      let resource = selectedResource;

      if (resource === 'event') {
        resource = 'flow-stage-types/' + flowStageType.name;
      }

      return service.getSchema(resource);
    },
    enabled: !!selectedResource && !isCustomForm,
  });

  // Parse schema fields from response. Enum-like fields expose a valuesUri (values
  // dropdown); date fields are selectable too so they can be compared temporally.
  const schemaFields = React.useMemo(() => {
    if (!schemaData || !Array.isArray(schemaData)) return [];
    return schemaData
      .filter(
        (field: any) =>
          field.meta?.valuesUri ||
          field.type === 'date' ||
          field.type === 'boolean' ||
          field.meta?.format === 'date',
      )
      .map((field: any) => ({
        value: field.name,
        label: field.meta?.label || field.label || field.name,
        valuesUri: field.meta?.valuesUri,
        // Numeric unix-seconds date fields (e.g. activity dates) advertise themselves
        // via meta.format so the editor offers date operators, not number handling.
        type: field.meta?.format === 'date' ? 'date' : field.type,
      }));
  }, [schemaData]);

  // Find the metadata (valuesUri / type) for the selected field
  const selectedFieldData = React.useMemo(() => {
    return schemaFields.find((field) => field.value === selectedField);
  }, [schemaFields, selectedField]);

  const selectedFieldType = isCustomForm
    ? selectedCustomField?.type
    : selectedFieldData?.type;
  const isDateField = selectedFieldType === 'date';
  const isBooleanField = selectedFieldType === 'boolean';

  // Operators offered depend on the selected field's type. On a lapse stage the
  // activity clause is the awaited signal, so its date operators read as the absence.
  const lapseActivityDate = isLapse && selectedResource === 'activity';
  const operatorOptions = isDateField
    ? lapseActivityDate
      ? LAPSE_DATE_OPERATOR_OPTIONS
      : DATE_OPERATOR_OPTIONS
    : isBooleanField
      ? BOOLEAN_OPERATOR_OPTIONS
      : STRING_OPERATOR_OPTIONS;

  const formOperator = useWatch({ control, name: `${prefix}.operator` });
  const isRelativeOperator = RELATIVE_OPERATORS.includes(formOperator);

  // Fetch field values for selected resource and field
  const { data: fieldValuesData } = useQuery({
    queryKey: [
      'fieldValues',
      selectedResource,
      selectedField,
      selectedFieldData?.valuesUri,
    ],
    queryFn: () =>
      service.getResourceFieldValues(
        selectedResource,
        selectedField,
        selectedFieldData?.valuesUri,
      ),
    enabled:
      !!selectedResource &&
      !!selectedField &&
      !isDateField &&
      !isBooleanField &&
      !isCustomForm,
  });

  // Parse field values from response
  const fieldValueOptions = React.useMemo<FieldOption[]>(() => {
    if (isCustomForm) return selectedCustomField?.choices ?? [];
    if (!fieldValuesData) return [];
    const values = Array.isArray(fieldValuesData)
      ? fieldValuesData
      : fieldValuesData.data || [];
    return values.map((item: any) => ({
      value: item.value,
      label: item.label,
    }));
  }, [fieldValuesData, isCustomForm, selectedCustomField]);

  // ── Control renderers (shared by both layouts) ──────────────────────────────

  const resourceSelect = (
    <Controller
      name={`${prefix}.resource`}
      control={control}
      render={({ field: { value, onChange } }) => (
        <Select
          value={RESOURCE_OPTIONS.find((opt) => opt.value === value) || null}
          onChange={(selected) => {
            onChange(selected?.value || '');
            setSelectedResource(selected?.value.toLowerCase() || '');
            setSelectedField(''); // Reset field when resource changes
            if (selected?.value) {
              requestAnimationFrame(() => scrollIntoView(fieldRef.current));
            }
          }}
          {...menuPortalProps}
          options={RESOURCE_OPTIONS}
          placeholder="Select resource..."
          isClearable
        />
      )}
    />
  );

  const fieldSelect = isCustomForm ? (
    <Controller
      name={`${prefix}.resourceField`}
      control={control}
      render={({ field: { onChange } }) => (
        <Select<CustomFormFieldOption>
          value={selectedCustomField ?? null}
          getOptionValue={(opt) => `${opt.customFormId}:${opt.key}`}
          onChange={(selected) => {
            onChange(selected?.key ?? '');
            setValue?.(`${prefix}.customFormId`, selected?.customFormId ?? '');
            setSelectedField(selected?.key ?? '');
            if (selected) {
              requestAnimationFrame(() => scrollIntoView(valueRef.current));
            }
          }}
          {...menuPortalProps}
          options={customFormFields}
          placeholder="Select field..."
          isClearable
          isLoading={false}
        />
      )}
    />
  ) : (
    <Controller
      name={`${prefix}.resourceField`}
      control={control}
      render={({ field: { value, onChange } }) => (
        <Select
          value={schemaFields.find((opt) => opt.value === value) || null}
          onChange={(selected) => {
            onChange(selected?.value || '');
            setSelectedField(selected?.value || '');
            if (selected?.value) {
              requestAnimationFrame(() => scrollIntoView(valueRef.current));
            }
          }}
          {...menuPortalProps}
          options={schemaFields}
          placeholder="Select field..."
          isClearable
          isLoading={!schemaData}
        />
      )}
    />
  );

  const operatorSelect = (
    <Controller
      name={`${prefix}.operator`}
      control={control}
      render={({ field: { value, onChange } }) => (
        <Select
          value={operatorOptions.find((opt) => opt.value === value) || null}
          onChange={(selected) => onChange(selected?.value || '')}
          {...menuPortalProps}
          options={operatorOptions}
          placeholder="Select operator..."
          isClearable
        />
      )}
    />
  );

  const valueControl =
    isDateField && isRelativeOperator ? (
      // Relative window: { amount, unit }
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange } }) => {
          const window = value && typeof value === 'object' ? value : {};
          return (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={window.amount ?? ''}
                onChange={(e) =>
                  onChange({
                    ...window,
                    amount:
                      e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                placeholder="Amount"
                className="w-24 rounded border px-2 py-1"
              />
              <div className="flex-1">
                <Select
                  value={
                    UNIT_OPTIONS.find((opt) => opt.value === window.unit) || null
                  }
                  onChange={(selected) =>
                    onChange({ ...window, unit: selected?.value })
                  }
                  {...menuPortalProps}
                  options={UNIT_OPTIONS}
                  placeholder="Unit..."
                />
              </div>
            </div>
          );
        }}
      />
    ) : isDateField ? (
      // Absolute date: before / after
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <input
            type="date"
            value={typeof value === 'string' ? value.slice(0, 10) : ''}
            onChange={(e) => onChange(e.target.value || '')}
            className="rounded border px-2 py-1"
          />
        )}
      />
    ) : isBooleanField ? (
      // Boolean: Yes / No
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            value={
              BOOLEAN_VALUE_OPTIONS.find((opt) => opt.value === value) || null
            }
            onChange={(selected) => onChange(selected?.value ?? null)}
            {...menuPortalProps}
            options={BOOLEAN_VALUE_OPTIONS}
            placeholder="Select..."
          />
        )}
      />
    ) : (
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            isMulti
            value={
              Array.isArray(value)
                ? fieldValueOptions.filter((opt) => value.includes(opt.value))
                : fieldValueOptions.find((opt) => opt.value === value) || null
            }
            onChange={(selected) => {
              if (Array.isArray(selected)) {
                onChange(selected.map((s) => s.value));
              }
            }}
            {...menuPortalProps}
            options={fieldValueOptions}
            placeholder="Select value(s)..."
            isClearable
            isLoading={!isCustomForm && !fieldValuesData}
          />
        )}
      />
    );

  // ── Compact grid row (drawer, resource-grouped) ─────────────────────────────

  if (isRow) {
    return (
      <div className="grid grid-cols-[22px_minmax(0,1.1fr)_minmax(0,0.8fr)_minmax(0,1.5fr)_26px] items-center gap-2 py-1">
        <div className="text-xs text-zinc-400">{displayIndex ?? index + 1}</div>
        <div className="min-w-0">{selectedResource ? fieldSelect : null}</div>
        <div className="min-w-0">{selectedField ? operatorSelect : null}</div>
        <div className="min-w-0">
          {selectedField && formOperator ? valueControl : null}
        </div>
        <div className="text-center">
          {showRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-zinc-400 hover:text-red-500"
              title="Remove condition"
            >
              &times;
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Stacked, labelled column (sidebar) ──────────────────────────────────────

  return (
    <div className="relative space-y-4 rounded border p-3">
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
          title="Remove condition"
        >
          &times;
        </button>
      )}

      {!hideResource && (
        <div>
          <label className="mb-2 block text-sm font-medium">Resource</label>
          {resourceSelect}
        </div>
      )}

      {selectedResource && (
        <div ref={fieldRef}>
          <label className="mb-2 block text-sm font-medium">Field</label>
          {fieldSelect}
        </div>
      )}

      {selectedField && (
        <div>
          <label className="mb-2 block text-sm font-medium">Operator</label>
          {operatorSelect}
        </div>
      )}

      {selectedField && formOperator && (
        <div ref={valueRef}>
          <label className="mb-2 block text-sm font-medium">
            {isDateField && isRelativeOperator ? 'Window' : 'Value(s)'}
          </label>
          {valueControl}
        </div>
      )}
    </div>
  );
};
